import TextField from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import Year, { InvalidYearError } from "common/values/year/year";
import { forwardRef, useState, useEffect } from "react";

const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
    width: '100%'
  },
  marginBottom: theme.spacing(1.5),
  minWidth: theme.spacing(40),
  ":first-of-type": {
    marginRight: theme.spacing(1.5)
  }
}));

type YearInputProps = {
  className?: string;
  variant?: TextFieldVariants;
  year?: Year;
  notInPast?: boolean;
  notInFuture?: boolean;
  label?: string;
  onChange?: (year?: Year, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const YearInput = forwardRef(
  function YearInput(props: YearInputProps, _ref) {
  const { className, variant, year, notInPast, notInFuture, label, onChange, onKeyDown } = props;

  const [yearFieldText, setYearFieldText] = useState<string>(year?.value ?? '');
  const [yearFieldError, setYearFieldError] = useState<string>('');

  useEffect(() => {
    setYearFieldText(year?.value ?? '');
  }, [year]);

  function buildYear(inputText?: string): Year | undefined {
    try {
      if (!inputText) throw new InvalidYearError("Year  is required");
      return new Year(inputText, notInPast, notInFuture);
    } catch (error) {
      if (error instanceof InvalidYearError) {
        throw error;
      }
    }
  }

  function handleYearChanged(year?: string) {
    let newYear: Year | undefined;
    let newErrors: string = '';

    try {
      newYear = buildYear(year);
      setYearFieldError('');
    } catch (error) {
      if (!(error instanceof InvalidYearError)) return;
      newErrors = error.message;
      setYearFieldError(newErrors);
    } finally {
      onChange?.(newYear, newErrors);
    }
  }

  return (
    <InputField
      className={className}
      label={label ?? "Year"}
      onChange={(event) => {
        setYearFieldText(event.target.value);
        handleYearChanged(event.target.value);
      }}
      required
      value={yearFieldText}
      error={!!yearFieldError}
      helperText={yearFieldError}
      variant={variant}
      onKeyDown={onKeyDown}
    />
  );
});

export default YearInput;
